import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Loader2, AlertTriangle, Users, Calendar, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { analyzeDocument } from "@/lib/analysis.functions";
import { ocrPages } from "@/lib/ocr.functions";
import { ScannedPdfBanner } from "@/components/app/scanned-pdf-banner";
import { AiMarkdown } from "@/components/app/ai-markdown";

export const Route = createFileRoute("/_authenticated/app/analysis")({
  component: AnalysisPage,
});

type Analysis = {
  documentType?: string;
  summary: string;
  parties: { name: string; role: string }[];
  keyDates: { date: string; description: string }[];
  clauses: { title: string; quote: string; comment: string }[];
  risks: { severity: "low" | "medium" | "high"; title: string; description: string; mitigation?: string }[];
  suggestions: string[];
};

function AnalysisPage() {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [scanned, setScanned] = useState(false);
  const pdfRef = useRef<any>(null);
  const analyzeFn = useServerFn(analyzeDocument);
  const ocrFn = useServerFn(ocrPages);

  const runAnalysis = async (text: string, sourceName: string) => {
    setLoading(true);
    try {
      const res = await analyzeFn({ data: { text, sourceName } });
      if (!res.ok) toast.error(res.error);
      else setAnalysis(res.analysis as Analysis);
    } finally { setLoading(false); }
  };

  const onDrop = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) return toast.error(t("pages.analysis.tooLarge"));
    setFile(f);
    setAnalysis(null);
    setScanned(false);
    setLoading(true);
    try {
      const arrayBuf = await f.arrayBuffer();
      const pdfjs: any = await import("pdfjs-dist");
      try {
        const ver = (pdfjs as any).version || "5.7.284";
        pdfjs.GlobalWorkerOptions.workerSrc =
          `https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/build/pdf.worker.min.mjs`;
      } catch (we) {
        console.warn("pdf worker init failed", we);
      }
      const loadingTask = pdfjs.getDocument({ data: arrayBuf });
      const pdf = await loadingTask.promise;
      pdfRef.current = pdf;
      const extracted: string[] = [];
      const numPages = pdf?.numPages ?? 0;
      for (let i = 1; i <= numPages; i++) {
        try {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const items = Array.isArray(content?.items) ? content.items : [];
          const text = items.map((it: any) => (typeof it?.str === "string" ? it.str : "")).join(" ");
          extracted.push(text);
        } catch (pe) {
          console.warn(`pdf page ${i} extract failed`, pe);
          extracted.push("");
        }
      }
      setPages(extracted);
      const fullText = extracted.join("\n\n--- PAGINA ---\n\n").trim();
      if (fullText.length < 50) {
        setScanned(true);
        setLoading(false);
        toast.message(t("pages.analysis.scannedToast"));
        return;
      }
      await runAnalysis(fullText, f.name);
    } catch (e: any) {
      console.error("PDF parse error", e);
      toast.error(e?.message ? t("pages.analysis.pdfErrorPrefix", { msg: e.message }) : t("pages.analysis.analysisError"));
      setLoading(false);
    }
  }, [analyzeFn, t]);

  const handleOcr = async (mode: "text" | "tables" = "text") => {
    if (!pdfRef.current || !file) return;
    setOcrLoading(true);
    try {
      const pdf = pdfRef.current;
      const total = Math.min(pdf.numPages, 20);
      const images: string[] = [];
      for (let i = 1; i <= total; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d")!;
        await page.render({ canvasContext: ctx, viewport }).promise;
        images.push(canvas.toDataURL("image/jpeg", 0.7));
      }
      const r = await ocrFn({ data: { pages: images, sourceName: file.name, mode } });
      if (!r.ok) { toast.error(r.error); return; }
      setPages(r.text.split(/\n\n/));
      setScanned(false);
      await runAnalysis(r.text, file.name);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message ?? t("pages.analysis.ocrError"));
    } finally { setOcrLoading(false); }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false, accept: { "application/pdf": [".pdf"] },
  });

  const reset = () => { setFile(null); setPages([]); setAnalysis(null); setScanned(false); pdfRef.current = null; };

  return (
    <>
      <PageHeader
        title={t("pages.analysis.title")}
        description={t("pages.analysis.description")}
        actions={file && <Button variant="outline" size="sm" onClick={reset}><RotateCcw className="mr-2 h-4 w-4" /> {t("pages.analysis.newBtn")}</Button>}
      />
      <PageBody>
        {!file ? (
          <Card {...getRootProps()}
            className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-16 text-center transition-colors ${
              isDragActive ? "border-accent bg-accent-muted/20" : "hover:border-accent/50"
            }`}>
            <input {...getInputProps()} />
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Upload className="h-8 w-8" />
            </div>
            <h3 className="font-display text-xl font-semibold">
              {isDragActive ? t("pages.analysis.drop") : t("pages.analysis.uploadPdf")}
            </h3>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              {t("pages.analysis.uploadDesc")}
            </p>
            <p className="mt-3 text-xs text-muted-foreground">{t("pages.analysis.uploadHint")}</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {scanned && (
              <ScannedPdfBanner
                pageCount={pages.length}
                ocrLoading={ocrLoading}
                onRunOcr={handleOcr}
                onReupload={reset}
                onPasteText={(t) => { setScanned(false); setPages([t]); runAnalysis(t, file.name); }}
              />
            )}
            <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
              <Card className="h-[75vh] overflow-auto p-0">
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-4 py-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">{file.name}</span>
                  <Badge variant="secondary" className="ml-auto">{t("pages.analysis.pages", { n: pages.length })}</Badge>
                </div>
                <div className="space-y-4 p-4">
                  {pages.map((p, i) => (
                    <div key={i} id={`pdf-page-${i + 1}`} className="rounded-md border bg-muted/20 p-4">
                      <div className="mb-2 text-xs font-medium text-muted-foreground">{t("pages.analysis.pageN", { n: i + 1 })}</div>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{p || <em>{t("pages.analysis.emptyPage")}</em>}</p>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="h-[75vh] overflow-auto">
                {loading ? (
                  <Card className="flex h-full flex-col items-center justify-center gap-3 p-12">
                    <Loader2 className="h-8 w-8 animate-spin text-accent" />
                    <p className="text-sm text-muted-foreground">{t("pages.analysis.analyzing")}</p>
                  </Card>
                ) : analysis ? (
                  <Card className="h-full overflow-hidden">
                    <Tabs defaultValue="summary" className="flex h-full flex-col">
                      <TabsList className="m-3 grid grid-cols-5">
                        <TabsTrigger value="summary"><Sparkles className="mr-1 h-3 w-3" />{t("pages.analysis.tabSummary")}</TabsTrigger>
                        <TabsTrigger value="parties"><Users className="mr-1 h-3 w-3" />{t("pages.analysis.tabParties")}</TabsTrigger>
                        <TabsTrigger value="dates"><Calendar className="mr-1 h-3 w-3" />{t("pages.analysis.tabDates")}</TabsTrigger>
                        <TabsTrigger value="clauses">{t("pages.analysis.tabClauses")}</TabsTrigger>
                        <TabsTrigger value="risks"><AlertTriangle className="mr-1 h-3 w-3" />{t("pages.analysis.tabRisks")}</TabsTrigger>
                      </TabsList>
                      <div className="flex-1 overflow-auto px-5 pb-5">
                        <TabsContent value="summary" className="space-y-4">
                          {analysis.documentType && (
                            <Badge className="bg-accent/20 text-accent-foreground">{analysis.documentType}</Badge>
                          )}
                          <AiMarkdown className="text-sm">{analysis.summary}</AiMarkdown>
                          {analysis.suggestions?.length > 0 && (
                            <div>
                              <h4 className="mb-2 font-display font-semibold">{t("pages.analysis.suggestedActions")}</h4>
                              <ul className="space-y-1.5 text-sm">
                                {analysis.suggestions.map((s, i) => (
                                  <li key={i} className="flex gap-2"><span className="text-accent">→</span>{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </TabsContent>
                        <TabsContent value="parties" className="space-y-2">
                          {analysis.parties.map((p, i) => (
                            <div key={i} className="rounded-md border p-3">
                              <div className="font-medium">{p.name}</div>
                              <div className="text-xs text-muted-foreground">{p.role}</div>
                            </div>
                          ))}
                        </TabsContent>
                        <TabsContent value="dates" className="space-y-2">
                          {analysis.keyDates.map((d, i) => (
                            <div key={i} className="flex gap-3 rounded-md border p-3">
                              <div className="font-mono text-sm font-medium text-accent">{d.date}</div>
                              <div className="text-sm">{d.description}</div>
                            </div>
                          ))}
                        </TabsContent>
                        <TabsContent value="clauses" className="space-y-3">
                          {analysis.clauses.map((c, i) => (
                            <div key={i} className="rounded-md border p-4">
                              <div className="font-display font-semibold">{c.title}</div>
                              <blockquote className="mt-2 border-l-2 border-accent pl-3 text-sm italic text-muted-foreground">
                                {c.quote}
                              </blockquote>
                              <p className="mt-2 text-sm">{c.comment}</p>
                            </div>
                          ))}
                        </TabsContent>
                        <TabsContent value="risks" className="space-y-3">
                          {analysis.risks.map((r, i) => (
                            <div key={i} className="rounded-md border p-4">
                              <div className="flex items-center gap-2">
                                <Badge variant={r.severity === "high" ? "destructive" : r.severity === "medium" ? "default" : "secondary"}>
                                  {r.severity === "high" ? t("pages.analysis.sevHigh") : r.severity === "medium" ? t("pages.analysis.sevMedium") : t("pages.analysis.sevLow")}
                                </Badge>
                                <span className="font-medium">{r.title}</span>
                              </div>
                              <p className="mt-2 text-sm">{r.description}</p>
                              {r.mitigation && (
                                <p className="mt-2 rounded bg-accent-muted/30 p-2 text-xs">
                                  <strong>{t("pages.analysis.mitigation")}:</strong> {r.mitigation}
                                </p>
                              )}
                            </div>
                          ))}
                        </TabsContent>
                      </div>
                    </Tabs>
                  </Card>
                ) : scanned ? null : (
                  <Card className="flex h-full items-center justify-center p-12">
                    <p className="text-sm text-muted-foreground">{t("pages.analysis.waiting")}</p>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
